/**
 * ActivityDetailPage - 行程詳情頁
 * 路由：/trips/:id/itinerary/:itineraryId
 */

import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Clock, MapPin, MapPinned, Pencil, Trash2, Map, PersonStanding, Lightbulb, Clipboard } from 'lucide-react';
import { getGoogleMapsUrl } from '../utils/googleMaps';
import { itineraryService } from '../services/itineraryService';
import { apiGet } from '../services/apiClient';
import { convertItineraryToActivity } from '../utils/itineraryConverter';
import { addDays } from '../utils/dates';
import type { Activity, CreateActivityInput } from '../types/activity';
import type { TripDetailResponse } from '../types/trip';
import TripMapView from '../components/trips/TripMapView';
import Modal from '../components/common/Modal';
import ActivityForm from '../components/activity/ActivityForm';
import type { ActivityFormHandle } from '../components/activity/ActivityForm';
import DesktopSidebar from '../components/common/DesktopSidebar';
import LoadingScreen from '../components/common/LoadingScreen';
import { getPlacePhotoUrl } from '../utils/photoUrl';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { getActivityTypeConfig } from '../utils/activityTypes';


export default function ActivityDetailPage() {
  const { id: tripId, itineraryId } = useParams<{ id: string; itineraryId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dayParam = searchParams.get('day');
  const backTo = `/trips/${tripId}${dayParam ? `?day=${dayParam}` : ''}`;
  const [activity, setActivity] = useState<Activity | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [tripStartDate, setTripStartDate] = useState<Date>(new Date());
  const [maxDays, setMaxDays] = useState(1);
  const [tripName, setTripName] = useState('');
  const [selectedMapActivityId, setSelectedMapActivityId] = useState<string | null>(null);
  // 手機/桌面兩份地圖都在 DOM 裡（靠 CSS 切換），只渲染當下版面那份，避免重複載入 Google Map
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const activityFormRef = useRef<ActivityFormHandle>(null);
  const photoUrl = getPlacePhotoUrl(activity?.placeId);

  useEffect(() => {
    if (!tripId || !itineraryId) return;

    const load = async () => {
      try {
        const [dto, tripResp] = await Promise.all([
          itineraryService.getDetail(itineraryId),
          apiGet<TripDetailResponse>(`/trip/${tripId}`),
        ]);
        setActivity(convertItineraryToActivity(dto, tripId));
        if (tripResp.exitCode === 1 && tripResp.data) {
          const td = tripResp.data;
          const start = new Date(td.startDate);
          const end = new Date(td.endDate);
          setTripStartDate(start);
          setTripName(td.name);
          const days = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
          setMaxDays(days);
        }
      } catch (err) {
        console.error('Failed to load activity detail:', err);
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [tripId, itineraryId]);

  const convertTimeForUpdate = (time: string | undefined, act: Activity): string | undefined => {
    if (!time) return undefined;
    const daySequence = act.dayIndex !== null ? act.dayIndex + 1 : 1;
    const dayDate = addDays(tripStartDate, daySequence - 1);
    const dateStr = dayDate.toISOString().split('T')[0];
    return `${dateStr} ${time}:00`;
  };

  const handleEditSubmit = async (data: CreateActivityInput) => {
    if (!activity) return;
    try {
      await itineraryService.update({
        itineraryId: activity.id,
        title: data.name,
        startTime: convertTimeForUpdate(data.startTime, activity),
        endTime: convertTimeForUpdate(data.endTime, activity),
        locationName: data.location,
        latitude: data.lat,
        longitude: data.lng,
        address: data.address,
        placeId: data.placeId,
        photoReference: data.photoReference,
        note: data.note,
        type: data.type,
      });
      const dto = await itineraryService.getDetail(activity.id);
      setActivity(convertItineraryToActivity(dto, tripId!));
      setIsFormOpen(false);
    } catch (err) {
      console.error('Failed to update activity:', err);
    }
  };

  const handleDelete = async () => {
    if (!activity) return;
    try {
      await itineraryService.delete(activity.id);
      navigate(backTo);
    } catch (err) {
      console.error('Failed to delete activity:', err);
    }
  };

  if (isLoading) {
    return <LoadingScreen className="h-screen" />;
  }

  if (!activity) {
    return (
      <div className="h-screen bg-white flex items-center justify-center">
        <div className="text-gray-500">找不到此行程</div>
      </div>
    );
  }

  const typeLabel = getActivityTypeConfig(activity.type).label;
  const mapActivities = activity.lat != null ? [activity] : [];
  const dayLabel = activity.dayIndex !== null ? `Day ${activity.dayIndex + 1}` : null;
  const googleMapsUrl = getGoogleMapsUrl(activity);
  const handleNavigate = () => {
    if (googleMapsUrl) window.open(googleMapsUrl, '_blank');
  };

  return (
    <div className="h-screen flex overflow-hidden bg-white">
      {/* Desktop Sidebar */}
      <DesktopSidebar onNavigate={navigate} tripId={tripId} />

      {/* Content */}
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">

        {/* ── Mobile: top map ── */}
        <div className="relative shrink-0 lg:hidden" style={{ height: '50vh' }}>
          {!isDesktop && (
            <TripMapView
              activities={mapActivities}
              selectedActivityId={selectedMapActivityId}
              onMarkerClick={(id) => setSelectedMapActivityId(id || null)}
              onMapClick={() => setSelectedMapActivityId(null)}
            />
          )}

          {/* 返回按鈕 */}
          <button
            onClick={() => navigate(backTo)}
            className="absolute top-12 left-4 z-10 flex size-10 items-center justify-center rounded-full bg-white shadow-md"
          >
            <svg className="w-5 h-5 text-slate-900" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          {/* 浮動資訊卡 */}
          <div
            className="absolute bottom-4 left-4 z-10 bg-white rounded-2xl shadow-lg px-4 py-3"
            style={{ maxWidth: '200px' }}
          >
            <p className="text-slate-900 text-sm font-bold leading-tight">{activity.name}</p>
            {(activity.address || activity.location) && (
              <div className="flex items-center gap-1.5 mt-1.5">
                <svg className="w-3 h-3 text-[#0dccf2] shrink-0" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                </svg>
                <p className="text-slate-500 text-xs leading-tight line-clamp-1">
                  {activity.address || activity.location}
                </p>
              </div>
            )}
            {activity.startTime && (
              <div className="flex items-center gap-1.5 mt-1">
                <svg className="w-3 h-3 text-[#0dccf2] shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-slate-500 text-xs">{activity.startTime}</p>
              </div>
            )}
          </div>
        </div>

        {/* ── Mobile: bottom details ── */}
        <div className="flex-1 overflow-y-auto lg:hidden bg-white">
          <div className="px-5 pt-5 pb-10">
            <button
              onClick={() => navigate(backTo)}
              className="flex items-center gap-1.5 text-slate-400 text-sm mb-4"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              返回列表
            </button>

            <div className="flex items-start justify-between mb-1">
              <h1 className="text-2xl font-bold text-slate-900 flex-1 mr-3 leading-tight">
                {activity.name}
              </h1>
            </div>

            <p className="text-slate-400 text-sm mb-5">
              {typeLabel}
              {activity.location ? ` · ${activity.location}` : ''}
            </p>

            <div className="grid grid-cols-2 gap-3 mb-5">
              {(activity.startTime || activity.endTime) && (
                <div className="bg-slate-50 rounded-xl p-3.5">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <svg className="w-3.5 h-3.5 text-[#0dccf2]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-slate-400 text-xs">時間</p>
                  </div>
                  <p className="text-slate-900 text-base font-semibold">
                    {activity.startTime}
                    {activity.endTime && ` – ${activity.endTime}`}
                  </p>
                </div>
              )}
              {(activity.address || activity.location) && (
                <div className="bg-slate-50 rounded-xl p-3.5">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <svg className="w-3.5 h-3.5 text-[#0dccf2]" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                    </svg>
                    <p className="text-slate-400 text-xs">地址</p>
                  </div>
                  <p className="text-slate-900 text-sm font-semibold leading-snug">
                    {activity.address || activity.location}
                  </p>
                </div>
              )}
            </div>

            {activity.note && (
              <div className="mb-6">
                <p className="text-slate-900 text-base font-semibold mb-2">備註</p>
                <p className="text-slate-500 text-sm leading-relaxed whitespace-pre-wrap">
                  {activity.note}
                </p>
              </div>
            )}

            <div className="flex justify-center gap-3">
              {googleMapsUrl && (
                <button
                  onClick={handleNavigate}
                  title="在地圖上開啟"
                  className="flex size-[54px] shrink-0 items-center justify-center rounded-full bg-[#0dccf2] text-white shadow-lg shadow-[#0dccf2]/30 hover:bg-opacity-90 transition-all"
                >
                  <MapPinned size={20} />
                </button>
              )}
              <button
                onClick={() => setIsFormOpen(true)}
                title="編輯"
                className="flex size-[54px] shrink-0 items-center justify-center rounded-full border border-slate-200 text-slate-700 hover:bg-slate-50 transition-colors"
              >
                <Pencil size={20} />
              </button>
              <button
                onClick={handleDelete}
                title="刪除"
                className="flex size-[54px] shrink-0 items-center justify-center rounded-full border border-red-100 text-red-400 hover:bg-red-50 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {isFormOpen ? (
          /* ── Desktop: Edit Form — two-panel layout (見 tripways.pen node CMWJY) ── */
          <div className="hidden lg:flex flex-col flex-1 overflow-hidden bg-[#f8f9fa]">
            {/* Top Bar */}
            <div className="flex items-center gap-4 px-10 py-5 bg-white border-b border-slate-100 shrink-0">
              <button
                onClick={() => setIsFormOpen(false)}
                className="flex items-center gap-1.5 rounded-[10px] border border-slate-200 px-3 py-2 text-sm font-medium text-slate-900 hover:bg-slate-50 transition-colors shrink-0"
              >
                <ArrowLeft size={18} />
                返回
              </button>
              <h1 className="text-slate-900 text-[22px] font-bold leading-tight">編輯行程</h1>
              <div className="flex items-center gap-1.5 rounded-full bg-slate-100 px-3.5 py-1.5">
                <Map size={14} className="text-slate-400 shrink-0" />
                <span className="text-slate-500 text-sm">{tripName}</span>
              </div>
            </div>

            {/* Body: Form + Preview */}
            <div className="flex flex-1 min-h-0">
              {/* Form Panel */}
              <div className="flex flex-col flex-1 min-h-0 overflow-hidden bg-white">
                <div className="flex-1 overflow-y-auto">
                  <ActivityForm
                    ref={activityFormRef}
                    activity={activity}
                    tripId={tripId!}
                    tripStartDate={tripStartDate}
                    maxDays={maxDays}
                    isEditMode
                    tripName={tripName}
                    desktopMode
                    onSubmit={handleEditSubmit}
                    onCancel={() => setIsFormOpen(false)}
                  />
                </div>
                <div className="shrink-0 flex items-center gap-3 px-12 py-6 border-t border-slate-100 bg-white">
                  <button
                    type="button"
                    onClick={() => setIsFormOpen(false)}
                    className="flex items-center justify-center rounded-xl border border-slate-200 px-7 py-3.5 text-sm font-medium text-slate-900 hover:bg-slate-50 transition-colors"
                  >
                    取消
                  </button>
                  <button
                    type="button"
                    onClick={() => activityFormRef.current?.submit()}
                    className="flex items-center justify-center rounded-xl bg-[#0dccf2] px-9 py-3.5 text-sm font-semibold text-white hover:bg-[#0bbfde] transition-colors"
                  >
                    更新
                  </button>
                </div>
              </div>

              {/* Preview Panel */}
              <aside className="w-[420px] shrink-0 flex flex-col gap-6 overflow-y-auto border-l border-slate-100 bg-white px-7 py-8">
                <div>
                  <h2 className="text-slate-900 text-lg font-bold mb-1">預覽</h2>
                  <p className="text-[#999] text-sm leading-normal">預覽這個行程在列表中的樣子</p>
                </div>

                <div className="flex items-center gap-3.5 rounded-2xl border border-[#E8E8E8] bg-white px-4 py-4 shadow-sm">
                  <div className="flex items-center justify-center rounded-full bg-[#E0F7FA] shrink-0" style={{ width: 44, height: 44 }}>
                    <PersonStanding size={22} className="text-[#0dccf2]" />
                  </div>
                  <div className="flex flex-col gap-1.5 min-w-0">
                    <span className="text-[#0dccf2] text-xs font-bold">--:-- --</span>
                    <span className="text-[#CCC] text-base font-semibold">行程名稱</span>
                    <span className="text-[#CCC] text-sm">地點 • 詳細資訊</span>
                  </div>
                </div>

                <div className="h-px bg-[#F0F0F0]" />

                <div className="flex flex-col gap-3">
                  <h3 className="text-slate-900 text-[15px] font-semibold">小提醒</h3>
                  <div className="flex flex-col gap-3">
                    <div className="flex items-start gap-2.5">
                      <Lightbulb size={16} className="text-amber-500 mt-0.5 shrink-0" />
                      <p className="text-[#6D6C6A] text-sm leading-normal">加上明確的時間，讓行程更有條理</p>
                    </div>
                    <div className="flex items-start gap-2.5">
                      <MapPin size={16} className="text-[#0dccf2] mt-0.5 shrink-0" />
                      <p className="text-[#6D6C6A] text-sm leading-normal">填寫地址，出門時就能直接導航</p>
                    </div>
                    <div className="flex items-start gap-2.5">
                      <Clipboard size={16} className="text-violet-500 mt-0.5 shrink-0" />
                      <p className="text-[#6D6C6A] text-sm leading-normal">用備註記下訂位代號或特殊注意事項</p>
                    </div>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        ) : (
          <>
            {/* ── Desktop: detail panel ── */}
            <div className="hidden lg:flex flex-col flex-1 overflow-hidden bg-white border-r border-slate-100">
              {/* Top Bar */}
              <div className="flex items-center gap-4 px-10 py-5 border-b border-slate-100 shrink-0">
                <button
                  onClick={() => navigate(backTo)}
                  className="flex items-center gap-1.5 rounded-[10px] border border-slate-200 px-3 py-2 text-sm font-medium text-slate-900 hover:bg-slate-50 transition-colors shrink-0"
                >
                  <ArrowLeft size={18} />
                  返回列表
                </button>
                <div className="flex items-center gap-1.5 text-sm min-w-0">
                  <span className="text-slate-400 truncate">{tripName}</span>
                  {dayLabel && (
                    <>
                      <span className="text-slate-400 shrink-0">/</span>
                      <span className="text-slate-400 shrink-0">{dayLabel}</span>
                    </>
                  )}
                  <span className="text-slate-400 shrink-0">/</span>
                  <span className="text-slate-900 font-semibold truncate">{activity.name}</span>
                </div>
              </div>

              {/* Main Body */}
              <div className="flex-1 overflow-y-auto px-10 py-8 flex flex-col gap-7 max-w-[640px]">
                {/* Title Row */}
                <div className="flex items-center justify-between gap-4">
                  <div className="flex flex-col gap-1.5 min-w-0">
                    <h1 className="text-[28px] font-bold text-slate-900 tracking-tight leading-tight">
                      {activity.name}
                    </h1>
                    <p className="text-slate-400 text-sm">
                      {typeLabel}
                      {activity.location ? ` · ${activity.location}` : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-2.5 shrink-0">
                    {googleMapsUrl && (
                      <button
                        onClick={handleNavigate}
                        title="在地圖上開啟"
                        className="flex size-12 items-center justify-center rounded-xl bg-[#0dccf2] text-white hover:bg-[#0bbfde] transition-colors"
                      >
                        <MapPinned size={16} />
                      </button>
                    )}
                    <button
                      onClick={() => setIsFormOpen(true)}
                      title="編輯"
                      className="flex size-12 items-center justify-center rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50 transition-colors"
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      onClick={handleDelete}
                      className="flex size-12 items-center justify-center rounded-xl border border-red-200 text-red-400 hover:bg-red-50 transition-colors"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                {/* Photo */}
                {photoUrl && (
                  <div className="rounded-2xl overflow-hidden h-[260px] shrink-0">
                    <img
                      src={photoUrl}
                      alt={activity.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}

                {/* Info Grid */}
                {((activity.startTime || activity.endTime) || (activity.address || activity.location)) && (
                  <div className="flex gap-4">
                    {(activity.startTime || activity.endTime) && (
                      <div className="flex-1 flex flex-col gap-2.5 rounded-[14px] bg-slate-50 border border-[#F0F0F0] p-5">
                        <div className="flex items-center gap-2">
                          <Clock size={14} className="text-[#0dccf2]" />
                          <p className="text-slate-400 text-xs">時間</p>
                        </div>
                        <p className="text-slate-900 text-xl font-semibold">
                          {activity.startTime}
                          {activity.endTime && ` – ${activity.endTime}`}
                        </p>
                      </div>
                    )}
                    {(activity.address || activity.location) && (
                      <div className="flex-1 flex flex-col gap-2.5 rounded-[14px] bg-slate-50 border border-[#F0F0F0] p-5">
                        <div className="flex items-center gap-2">
                          <MapPin size={14} className="text-[#0dccf2]" />
                          <p className="text-slate-400 text-xs">地址</p>
                        </div>
                        <p className="text-slate-900 text-base font-semibold leading-snug">
                          {activity.address || activity.location}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Notes Section */}
                {activity.note && (
                  <div className="flex flex-col gap-2.5">
                    <p className="text-slate-900 text-base font-semibold">備註</p>
                    <div className="rounded-[14px] bg-slate-50 border border-[#F0F0F0] px-5 py-4">
                      <p className="text-sm text-[#4A4A4A] leading-relaxed whitespace-pre-wrap">
                        {activity.note}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* ── Desktop: map panel ── */}
            <div className="hidden lg:block w-[480px] shrink-0 h-full p-5">
              <div className="rounded-2xl overflow-hidden w-full h-full">
                {isDesktop && (
                  <TripMapView
                    activities={mapActivities}
                    selectedActivityId={selectedMapActivityId}
                    onMarkerClick={(id) => setSelectedMapActivityId(id || null)}
                    onMapClick={() => setSelectedMapActivityId(null)}
                  />
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* 編輯 Modal（行動版限定 — 桌面版用上面的內嵌兩欄式表單） */}
      <div className="lg:hidden">
      <Modal
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        title="編輯行程"
        variant="bottom-sheet"
      >
        <ActivityForm
          activity={activity}
          tripId={tripId!}
          tripStartDate={tripStartDate}
          maxDays={maxDays}
          isEditMode
          onSubmit={handleEditSubmit}
          onCancel={() => setIsFormOpen(false)}
        />
      </Modal>
      </div>
    </div>
  );
}
